import * as React from "react";
import { useState } from "react";
import {
  Box, Typography,
  IconButton, Menu, MenuItem, Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CheckIcon from "@mui/icons-material/Check";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import {
  useGetFieldAdminDevicesQuery,
  useSetFieldAdminDeviceStatusMutation,
} from "./fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { OK_GREEN_TEXT, WARN_YELLOW, BAD_RED_TEXT, LOW_BATTERY_PERCENT, LOW_DATA_MB, teamLabel, formatDayTime } from "./designTokens";
import { useDispatch } from "react-redux";
import { showSuccess, showError } from "../../Global/notifications/notificationsSlice";
import { FAPageState } from "./FAStates";

import { FA_MENU_PAPER_SX, FAStatTile, FAStatusPill, FAEmptyState } from "./FALayout";
import { FAFilterChips, FAPageShell, FAPageHeader } from "./FALayout";
import { DeviceDialog, DecommissionConfirmDialog } from "./DeviceDialog";

// "On register" (not "All") reproduces the design's own default — decommissioned
// hidden — matching its data-scope="active" starting state.
const FILTERS = ["On register", "In field", "Spare", "Flagged", "Decommissioned"];
const GRID_COLUMNS = { xs: "1fr auto", sm: "minmax(0,1.7fr) 1fr 0.9fr 1fr 64px" };

// devices.html's .tbl-row rules under @media (max-width: 720px). The .col-installer and
// .col-reading cells are display:none on mobile, which removes
// them as grid items — so without named areas the remaining three auto-place and the
// status chip lands top-right where the actions belong, pushing the buttons onto a second
// row. The design puts status under the device and spans the actions down both rows.
const GRID_AREAS = { xs: '"main actions" "meta actions"', sm: "none" };

const isSpare = (device) => device.status === "in_field" && device.assigned_installer_id == null;

// "Flagged today" means today's reading was low — last_reading_is_today gates it, because
// the reading column itself shows the newest reading whenever it was taken.
const isFlagged = (device) =>
  device.status !== "decommissioned" &&
  device.last_reading_is_today &&
  ((device.last_reading_battery_percent != null && device.last_reading_battery_percent <= LOW_BATTERY_PERCENT) ||
    (device.last_reading_data_balance_mb != null && device.last_reading_data_balance_mb <= LOW_DATA_MB));

// "Spare" is a derived display state, not a stored status — a device is spare exactly
// when nobody has it. See the Device model for why it isn't a third stored value.
function statusMeta(device) {
  if (device.status === "decommissioned") return { label: "Decommissioned", bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" };
  if (isSpare(device)) return { label: "Spare", bg: "rgba(251,191,36,0.12)", color: WARN_YELLOW };
  return { label: "In field", bg: "rgba(34,197,94,0.12)", color: OK_GREEN_TEXT };
}

export default function Devices() {
  const { data, isSuccess, isError, error, refetch } = useGetFieldAdminDevicesQuery();
  const [activeFilter, setActiveFilter] = useState("On register");
  const [editingDevice, setEditingDevice] = useState(null); // null closed, {} add, device edit
  const [decommissionTarget, setDecommissionTarget] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // { anchorEl, device } or null

  if (!isSuccess) return <FAPageState isError={isError} error={error} onRetry={refetch} count={4} tiles={4} />;

  const devices = data.devices;
  const installers = data.installers;
  const nameByInstaller = {};
  installers.forEach((installer) => { nameByInstaller[installer.id] = installer.name; });

  const inFieldCount = devices.filter((d) => d.status === "in_field" && d.assigned_installer_id != null).length;
  const spareCount = devices.filter(isSpare).length;
  const flaggedCount = devices.filter(isFlagged).length;
  const decommissionedCount = devices.filter((d) => d.status === "decommissioned").length;

  const FILTER_PREDICATES = {
    "On register": (d) => d.status !== "decommissioned",
    "In field": (d) => d.status === "in_field" && d.assigned_installer_id != null,
    Spare: isSpare,
    Flagged: isFlagged,
    Decommissioned: (d) => d.status === "decommissioned",
  };
  const CHIP_COUNTS = {
    "On register": devices.length - decommissionedCount,
    "In field": inFieldCount,
    Spare: spareCount,
    Flagged: flaggedCount,
    Decommissioned: decommissionedCount,
  };
  const filtered = devices.filter(FILTER_PREDICATES[activeFilter]);

  return (
    <>
      <FAPageShell>
        {/* Page head */}
        <FAPageHeader
          title="Devices"
          sub={<><Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>{teamLabel(data.team_name, data.team_id)}</Box> · installer phones &amp; tablets</>}
          action={<Box
            onClick={() => setEditingDevice({})}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "7px", px: "14px", py: "9px",
              backgroundColor: "rgba(253,185,64,0.1)", border: "1px solid rgba(253,185,64,0.3)",
              borderRadius: "8px", color: AMBER, fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              "&:hover": { backgroundColor: "rgba(253,185,64,0.18)" },
            }}
          >
            <AddIcon sx={{ fontSize: "1.05rem" }} /> Add device
          </Box>}
        />

        {/* Stat tiles */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "10px", mb: "22px" }}>
          <FAStatTile label="In field" value={inFieldCount} sub="With an installer" />
          <FAStatTile label="Spare" value={spareCount} sub="Unassigned pool" />
          <FAStatTile label="Flagged today" value={flaggedCount} sub="Battery / data low" subColor={flaggedCount > 0 ? BAD_RED_TEXT : undefined} />
          <FAStatTile label="Decommissioned" value={decommissionedCount} sub="Returned to IMS" />
        </Box>

        {/* Filter chips */}
        <FAFilterChips filters={FILTERS} counts={CHIP_COUNTS} active={activeFilter} onChange={setActiveFilter} />

        {/* Section label */}
        <Box sx={{ py: "6px", mb: "4px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Device register
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>
            {devices.length - decommissionedCount} live · {decommissionedCount} decommissioned
          </Typography>
        </Box>

        {filtered.length === 0 ? (
          <FAEmptyState card py="70px">{devices.length === 0
                ? "No devices on the register yet — add a device to get started."
                : "No devices match this filter."}</FAEmptyState>
        ) : (
          <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
            <Box
              sx={{
                display: { xs: "none", sm: "grid" }, gridTemplateColumns: GRID_COLUMNS, gap: "10px", px: "16px", py: "11px",
                fontSize: "0.64rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em",
                color: "rgba(255,255,255,0.45)", borderBottom: `1px solid ${BORDER}`, backgroundColor: "rgba(255,255,255,0.015)",
              }}
            >
              <span>Device</span><span>Installer</span><span>Status</span><span>Last reading</span><span />
            </Box>
            {filtered.map((device, index) => (
              <DeviceRow
                key={device.id}
                device={device}
                installerName={nameByInstaller[device.assigned_installer_id]}
                isLast={index === filtered.length - 1}
                onEdit={() => setEditingDevice(device)}
                onMenuOpen={(anchorEl) => setRowMenu({ anchorEl, device })}
              />
            ))}
          </Box>
        )}
      </FAPageShell>

      <RowActionsMenu
        rowMenu={rowMenu}
        onClose={() => setRowMenu(null)}
        onEdit={setEditingDevice}
        onDecommission={setDecommissionTarget}
      />

      {editingDevice ? (
        <DeviceDialog device={editingDevice} installers={installers} onClose={() => setEditingDevice(null)} />
      ) : null}
      {decommissionTarget ? (
        <DecommissionConfirmDialog device={decommissionTarget} onClose={() => setDecommissionTarget(null)} />
      ) : null}
    </>
  );
}

function DeviceRow({ device, installerName, isLast, onEdit, onMenuOpen }) {
  const meta = statusMeta(device);
  const TypeIcon = device.device_type === "Tablet" ? TabletMacOutlinedIcon : PhoneAndroidOutlinedIcon;
  const battery = device.last_reading_battery_percent;
  const dataMb = device.last_reading_data_balance_mb;
  const hasReading = battery != null || dataMb != null;

  return (
    <Box
      onClick={onEdit}
      sx={{
        display: "grid", gridTemplateColumns: GRID_COLUMNS, gridTemplateAreas: GRID_AREAS, gap: { xs: "4px 10px", sm: "10px" }, alignItems: "center", px: "16px", py: "12px",
        fontSize: "0.86rem", borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.04)", cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(253,185,64,0.04)" },
      }}
    >
      <Box sx={{ gridArea: { xs: "main", sm: "auto" }, display: "flex", alignItems: "center", gap: "11px", minWidth: 0 }}>
        <Box sx={{ width: "34px", height: "34px", borderRadius: "9px", backgroundColor: "rgba(99,102,241,0.16)", color: "#A5B4FC", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <TypeIcon sx={{ fontSize: "1.05rem" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {device.fleet_id}
            </Typography>
            <Box component="span" sx={{ fontSize: "0.6rem", fontWeight: 700, px: "6px", py: "2px", borderRadius: "4px", backgroundColor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.04em", flexShrink: 0 }}>
              {device.device_type}
            </Box>
          </Box>
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "1px", fontVariantNumeric: "tabular-nums" }}>
            {device.asset_id || "No asset ID"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: { xs: "none", sm: "block" }, color: installerName ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.4)", fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {installerName || "Unassigned"}
      </Box>

      <Box sx={{ gridArea: { xs: "meta", sm: "auto" } }}>
        <FAStatusPill meta={meta} />
      </Box>

      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        {hasReading ? (
          <>
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.72)" }}>
              <Box component="span" sx={{ color: battery != null && battery <= LOW_BATTERY_PERCENT ? BAD_RED_TEXT : "#fff", fontWeight: 600 }}>
                {battery != null ? `${battery}%` : "—"}
              </Box>
              {" · "}
              <Box component="span" sx={{ color: dataMb != null && dataMb <= LOW_DATA_MB ? BAD_RED_TEXT : "#fff", fontWeight: 600 }}>
                {dataMb != null ? `${dataMb} MB` : "—"}
              </Box>
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)" }}>
              {formatDayTime(device.last_reading_at)}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.82rem" }}>—</Typography>
        )}
      </Box>

      <Box sx={{ gridArea: { xs: "actions", sm: "auto" }, display: "flex", alignItems: "center", gap: "2px", justifyContent: "flex-end" }}>
        <IconButton
          size="small"
          aria-label="Edit device"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: AMBER, backgroundColor: "rgba(255,255,255,0.05)" } }}
        >
          <EditOutlinedIcon sx={{ fontSize: "1.05rem" }} />
        </IconButton>
        <IconButton
          size="small"
          aria-label="More actions"
          onClick={(e) => { e.stopPropagation(); onMenuOpen(e.currentTarget); }}
          sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: AMBER, backgroundColor: "rgba(255,255,255,0.05)" } }}
        >
          <MoreVertIcon sx={{ fontSize: "1.05rem" }} />
        </IconButton>
      </Box>
    </Box>
  );
}

function RowActionsMenu({ rowMenu, onClose, onEdit, onDecommission }) {
  const dispatch = useDispatch();
  const [setStatus] = useSetFieldAdminDeviceStatusMutation();
  const device = rowMenu?.device;

  // These two actions emitted no feedback at all, so a failure was indistinguishable
  // from a success — the menu closed either way and the row only changed if it worked.
  const handleAction = async (body, successMessage) => {
    onClose();
    if (!device) return;
    const result = await setStatus({ device_id: device.id, ...body });
    dispatch(result.error ? showError("Couldn't update the device.") : showSuccess(successMessage));
  };

  return (
    <Menu
      anchorEl={rowMenu?.anchorEl}
      open={Boolean(rowMenu)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: FA_MENU_PAPER_SX }}
    >
      <MenuItem onClick={() => { onClose(); if (device) onEdit(device); }}>
        <EditOutlinedIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Edit device
      </MenuItem>
      {device?.status === "in_field" && device?.assigned_installer_id != null ? (
        // Spare is derived from having no installer, so "return to spare pool" is an
        // unassignment rather than a status change.
        <MenuItem onClick={() => handleAction({ assigned_installer_id: null }, "Device returned to the spare pool")}>
          <Inventory2OutlinedIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Return to spare pool
        </MenuItem>
      ) : null}
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      {device?.status === "decommissioned" ? (
        <MenuItem onClick={() => handleAction({ status: "in_field" }, "Device re-issued")}>
          <CheckIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Re-issue device
        </MenuItem>
      ) : (
        <MenuItem onClick={() => { onClose(); if (device) onDecommission(device); }} data-danger="true">
          <ArchiveOutlinedIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Decommission
        </MenuItem>
      )}
    </Menu>
  );
}

